<template>
  <div id="accordionSetupFeaturedImage" class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingSetupFeaturedImage" class="accordion-header">
        <button
          aria-controls="collapseSetupFeaturedImage"
          aria-expanded="true"
          class="accordion-button collapsed"
          data-bs-target="#collapseSetupFeaturedImage"
          data-bs-toggle="collapse"
          type="button"
        >
          <div class="mb-0 d-flex align-items-center">
            <div class="template-setup-accordion-icon me-8 d-flex align-items-center justify-content-center">
              <v-icon icon="description" size="xs" />
            </div>

            <div>
              <div class="h6 ff-secondary mb-0 fw-bold">{{ $t("clientTour.featuredImage") }}</div>
            </div>
          </div>
        </button>
      </h2>

      <div
        id="collapseSetupFeaturedImage"
        aria-labelledby="headingSetupFeaturedImage"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionSetupFeaturedImage"
      >
        <div class="accordion-body pb-16 pb-xl-24">
          <div v-if="hasFeaturedImage" class="featured-image-preview">
            <v-picture
              class="d-block featured-image-preview__picture cursor-pointer"
              :title="$t('clientTour.featuredImage')"
              @click="openImagePreview"
            >
              <img
                :alt="$t('clientTour.featuredImage')"
                :src="featuredImageUrl"
                @error="onImageError"
              />
            </v-picture>

            <v-button
              class="featured-image-preview__remove btn-icon"
              size="xs"
              variant="danger"
              :disabled="disableButton"
              :is-loading="disableButton"
              @click="removeFeaturedImage"
            >
              <v-icon icon="trash" size="sm" space="me-8" />
              {{ $t("general.button.remove") }}
            </v-button>
          </div>

          <template v-else>
            <v-uploader
              ref="uploader"
              :multiple="false"
              :max-items="1"
              library-filter-type="image"
              :after-upload="onChange"
              route-prefix="api/s3"
              @removed="onChange"
            />

            <div class="d-flex justify-content-end mt-12">
              <v-button
                :disabled="disableButton || mediaUuids.length === 0"
                class="btn-icon w-fit align-self-end"
                size="xs"
                variant="primary"
                @click="confirmMedia"
              >
                {{ $t("general.button.save") }}
                <v-icon icon="arrow-right" size="xs" space="ms-8" />
              </v-button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import api from "@/api";
import VUploader from "@/components/vendor/upload/VUploader.vue";
import Media from "@/api/objects/Media";
import VPicture from "@/components/vendor/basic/picture/VPicture.vue";
import Template from "@/api/objects/Template";
import { useToast } from "vue-toastification";
import { useUserStore } from "@/stores/user";

const props = defineProps<{
  agency: string;
  template: Template;
}>();

const emit = defineEmits<{
  (e: "reloadTemplate"): void;
}>();

const { t } = useI18n();
const toast = useToast();
const userStore = useUserStore();

const media = ref<any[]>([]);
const disableButton = ref(false);
const featuredImage = ref<Media | null>(null);
const uploader = ref<any>(null);

const mediaUuids = computed(() =>
  media.value.map((mediaItem: any) => ({
    uuid: mediaItem.attributes.uuid,
    name: mediaItem.attributes.name,
    collection: "activity_image",
  })),
);

const resolveFeaturedImage = (template: Template | null | undefined): Media | null => {
  const image = template?.featuredImage || (template as any)?.featured_image;
  if (!image) {
    return null;
  }
  return Array.isArray(image) ? image[0] : image;
};

const resolveMediaUrl = (image: Media | Record<string, any> | null | undefined): string => {
  if (!image) {
    return "";
  }
  return (
    image.storageUrl ||
    (image as any).storage_url ||
    image.originalUrl ||
    (image as any).original_url ||
    (image as any).previewUrl ||
    (image as any).preview_url ||
    (image as any).url ||
    ""
  );
};

const featuredImageUrl = computed(() => resolveMediaUrl(featuredImage.value));

const hasFeaturedImage = computed(() => {
  const image = featuredImage.value as any;
  return !!(image?.id && featuredImageUrl.value);
});

watch(
  () => props.template,
  (template) => {
    featuredImage.value = resolveFeaturedImage(template);
  },
  { immediate: true, deep: true },
);

const onChange = () => {
  if (uploader.value) {
    media.value = uploader.value.mediaLibrary.state.media;
  }
};

const onImageError = (event: Event) => {
  const target = event.target as HTMLImageElement | null;
  if (target) {
    target.src = "/img/placeholder-image.png";
  }
};

const openImagePreview = () => {
  if (featuredImageUrl.value) {
    window.open(featuredImageUrl.value, "_blank");
  }
};

const clearUploader = () => {
  media.value = [];
  if (uploader.value?.mediaLibrary) {
    uploader.value.mediaLibrary.state.media = [];
  }
};

const unwrapConfirmedMedia = (response: any): Media | null => {
  const candidates = [
    response,
    response?.data,
    response?.content,
    Array.isArray(response) ? response[0] : null,
    Array.isArray(response?.data) ? response.data[0] : null,
    Array.isArray(response?.content) ? response.content[0] : null,
  ];

  for (const candidate of candidates) {
    if (candidate?.id || candidate?.uuid) {
      return candidate as Media;
    }
  }

  return null;
};

const reloadTemplate = async () => {
  await userStore.setCurrentTemplateV2({
    agency: props.agency,
    id: props.template.id,
  });
  featuredImage.value = resolveFeaturedImage(userStore.currentTemplate);
  emit("reloadTemplate");
};

const removeFeaturedImage = async () => {
  if (!featuredImage.value?.id) {
    return;
  }

  try {
    disableButton.value = true;
    await api.media.delete("s3", featuredImage.value.id);
    featuredImage.value = null;
    clearUploader();
    await reloadTemplate();
    toast.success(t("general.shared.savedChanges"), {
      position: "bottom-right",
    });
  } catch (e: any) {
    console.error(e);
    toast.error(e.response?.data?.message || t("general.shared.errorMessage"), {
      position: "bottom-right",
    });
  } finally {
    disableButton.value = false;
  }
};

const confirmMedia = async () => {
  try {
    disableButton.value = true;

    const data = {
      media: mediaUuids.value,
      model_type: "activity",
      collection: "activity_image",
      model_id: props.template.id,
    };

    const response = await api.media.confirm("s3", data);
    const confirmedMedia = unwrapConfirmedMedia(response);

    toast.success(t("general.shared.savedChanges"), {
      position: "bottom-right",
    });

    clearUploader();

    if (confirmedMedia) {
      featuredImage.value = confirmedMedia;
    }

    await reloadTemplate();
  } catch (e: any) {
    console.error(e);
    toast.error(e.response?.data?.message || t("general.shared.errorMessage"), {
      position: "bottom-right",
    });
  } finally {
    disableButton.value = false;
  }
};
</script>

<style lang="scss" scoped>
.accordion-button,
.accordion-button:not(.collapsed) {
  background-color: transparent;
  padding: 0.5rem 1rem !important;
}

.featured-image-preview {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.featured-image-preview__picture {
  position: relative;
  width: 100%;
  max-width: 320px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #dee2e6;
  background: #f8f9fa;

  :deep(img) {
    display: block;
    width: 100%;
    max-height: 14rem;
    object-fit: cover;
  }
}

.featured-image-preview__remove {
  align-self: flex-start;
}
</style>
